import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { getDeck, getUserDecks } from "../utils/firestore";
import { useAuth } from "../contexts/AuthContext";
import Card from "../components/Card";
import StandardCard from "../components/StandardCard";
import UnlockModal from "../components/UnlockModal";
import CardNavigation from "../components/CardNavigation";
import UnlockButton from "../components/UnlockButton";
import { StandardDecks } from "../data/StandardDecks";
import { trackEvent } from "../utils/Analytics";

function DeckView() {
    const UNLOCK_PRICE = "$9.99"; // Example price, adjust as needed

    const { deckId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [deck, setDeck] = useState(null);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFlipped, setIsFlipped] = useState(false);
    const [showUnlockModal, setShowUnlockModal] = useState(false);
    const [userUnlockedDeckIds, setUserUnlockedDeckIds] = useState(new Set());

    // Fetch user's unlocked decks
    useEffect(() => {
        let isMounted = true;
        async function fetchUserDecks() {
            if (!currentUser) return;
            try {
                const userDecks = await getUserDecks();
                if (isMounted) {
                    setUserUnlockedDeckIds(new Set(userDecks.map(d => d.id)));
                }
            } catch (err) {
                // Optionally handle error
            }
        }
        fetchUserDecks();
        return () => { isMounted = false; };
    }, [currentUser]);

    const isStandardDeck = deck?.id === "standard-v1";
    // Deck is locked if it's a standard deck and not in user's unlocked decks
    const isLocked = isStandardDeck && !userUnlockedDeckIds.has(deckId);

    const successUrl = `${window.location.origin}/purchase/success?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = window.location.href;

    const TEST_MODE = false;
    const STANDARD_DIGITAL_STRIPE_PRICE_ID = TEST_MODE ? process.env.REACT_APP_STANDARD_DIGITAL_PRICE_ID_DEV : process.env.REACT_APP_STANDARD_DIGITAL_PRICE_ID_PROD;

    const cardsToShow = useMemo(() => {
        if (!deck?.cards) return [];
        return isLocked
            ? deck.cards.filter(card => card.trial)
            : deck.cards;
    }, [deck, isLocked]);

    useEffect(() => {
        let isMounted = true;

        async function fetchDeck() {
            const standardDeck = Array.isArray(StandardDecks)
                ? StandardDecks.find(d => d.id === deckId)
                : (StandardDecks.id === deckId ? StandardDecks : null);

            if (standardDeck) {
                setDeck(standardDeck);
                setLoading(false);
                return;
            }

            if (!currentUser || !deckId) return;

            try {
                const fetchedDeck = await getDeck(deckId);
                if (isMounted) {
                    setDeck(fetchedDeck);
                }
            } catch (err) {
                console.error("Error fetching deck:", err);
                if (isMounted) {
                    setError("Failed to load deck");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        fetchDeck();
        return () => {
            isMounted = false;
        };
    }, [deckId, currentUser]);

    useEffect(() => {
        document.body.style.overflow = showUnlockModal ? "hidden" : "";
    }, [showUnlockModal]);

    const handleUnlockDeck = async () => {
        if (!currentUser) {
            alert("You must be signed in to unlock a deck.");
            return;
        }

        setLoading(true);

        trackEvent('Deck', 'Unlock Deck Click', 'User clicked unlock standard deck', null, {
          user_id: currentUser?.uid,
          user_email: currentUser?.email,
          referrer: document.referrer,
          current_url: window.location.href,
        });

        try {
            const token = await currentUser.getIdToken();

            const res = await fetch(process.env.REACT_APP_CREATE_STRIPE_SESSION_URL_PROD, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    priceId: STANDARD_DIGITAL_STRIPE_PRICE_ID,
                    successUrl,
                    cancelUrl,
                    deckType: "standard-digital",
                    deckId, // <-- Pass the deckId here!
                }),
                mode: "cors",
            });

            const result = await res.json();

            if (!res.ok) {
                alert(result.error || "Something went wrong. Please try again.");
                setLoading(false);
                return;
            }

            window.location.href = result.url;
        } catch (err) {
            console.error("Error starting checkout:", err);
            alert("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    const handlePrevious = () => {
        setIsFlipped(false);
        setCurrentCardIndex(prev => Math.max(0, prev - 1));
    };

    const handleNext = () => {
        if (isLocked && currentCardIndex === cardsToShow.length - 1) {
            setShowUnlockModal(true);
            return;
        }

        setIsFlipped(false);
        setCurrentCardIndex(prev =>
            Math.min(cardsToShow.length - 1, prev + 1)
        );
    };

    if (loading) {
        return (
            <div className="flex flex-col pt-24 items-center justify-center min-h-screen">
                <div className="loading loading-spinner loading-lg"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col pt-24 items-center justify-center min-h-screen">
                <div className="alert alert-error">
                    <p>{error}</p>
                </div>
                <button className="btn btn-primary mt-4" onClick={() => navigate("/home")}>
                    Back to Home
                </button>
            </div>
        );
    }

    if (!deck?.cards?.length) {
        return (
            <div className="flex flex-col pt-24 items-center justify-center min-h-screen">
                <p>No cards found in this deck.</p>
                <button className="btn btn-primary mt-4" onClick={() => navigate("/home")}>
                    Back to Home
                </button>
            </div>
        );
    }

    const currentCard = cardsToShow[currentCardIndex];

    return (
        <div className="flex flex-col pt-16 items-center justify-center">
            <div className="w-full max-w-2xl px-4">

                {deck?.title && (
                    <h1 className="text-lg font-bold font-title text-darkgreen text-center mb-2">{deck.title}</h1>
                )}

                {isStandardDeck ? (
                    <StandardCard card={currentCard} />
                ) : (
                    <Card
                        card={currentCard}
                        isFlipped={isFlipped}
                        onFlip={() => setIsFlipped(!isFlipped)}
                    />
                )}

                <div className="flex flex-col items-center justify-center gap-4 mx-auto mt-4">
                    <CardNavigation
                        currentIndex={currentCardIndex}
                        numDisplayedCards={cardsToShow.length}
                        numCards={deck.cards.length}
                        category={currentCard?.category}
                        isLocked={isLocked}
                        onPrev={handlePrevious}
                        onNext={handleNext}
                    />
                    {isLocked && (
                        <div className="flex justify-center mb-2">
                            <UnlockButton price={UNLOCK_PRICE} onClick={handleUnlockDeck} />
                        </div>
                    )}
                </div>
            </div>

            {showUnlockModal && (
                <UnlockModal
                    price={UNLOCK_PRICE}
                    totalCards={deck.cards.length}
                    onClose={() => setShowUnlockModal(false)}
                    onUnlock={() => {
                        setShowUnlockModal(false);
                        handleUnlockDeck();
                    }}
                />
            )}
        </div>
    );
}

export default DeckView;
